import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
} from "framer-motion";
import { Nudge } from "../../../components/Nudges/Nudge";
import { useNudgeLocation } from "../../../apis/nudgeApi.hooks";
import { useNavigate } from "../../../hooks/useNavigate";

export function DashboardNudgeContainer() {
  const navigate = useNavigate();

  const { nudges, isLoading, error } = useNudgeLocation("core.dashboard.home", {
    minViewTime: 2000,
    threshold: 0.7,
  });

  if (isLoading) {
    return null;
  }

  if (error) {
    return null;
  }

  const visibleNudges = nudges.filter(({ isDismissed }) => !isDismissed);

  if (visibleNudges.length === 0) {
    return null;
  }

  return (
    <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-h-[120px] tw-my-auto">
      <div
        className="tw-relative tw-w-full tw-mt-4 tw-mb-2"
        style={{
          height: `${Math.max(120, 80 + (visibleNudges.length - 1) * 8)}px`,
        }}
      >
        <AnimatePresence>
          {visibleNudges.map(
            ({ nudge, trackClick, trackDismiss, trackingRef }, index) => {
              const handleClick = async () => {
                await trackClick();
                if (nudge.ctaAction) {
                  navigate(nudge.ctaAction);
                }
              };

              return (
                <AnimatedNudge
                  key={nudge.key}
                  nudge={nudge}
                  index={index}
                  total={visibleNudges.length}
                  onClick={handleClick}
                  onDismiss={trackDismiss}
                  trackingRef={index === 0 ? trackingRef : undefined}
                />
              );
            },
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function AnimatedNudge({
  nudge,
  index,
  total,
  onClick,
  onDismiss,
  trackingRef,
}: {
  nudge: any;
  index: number;
  total: number;
  onClick: () => void;
  onDismiss: () => Promise<void>;
  trackingRef?: (element: HTMLDivElement | null) => void;
}) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useTransform(mouseY, [-100, 100], [1, -1]);
  const rotateY = useTransform(mouseX, [-100, 100], [-1, 1]);

  const zIndex = total - index;
  const baseTranslateY = -index * 8;
  const baseScale = 1 - index * 0.015;
  const baseOpacity = Math.max(0.8, 1 - index * 0.05);

  return (
    <motion.div
      layout="position"
      className="tw-absolute tw-top-0 tw-left-0 tw-w-full"
      style={{
        zIndex,
        rotateX,
        rotateY,
        transform: `scale(${baseScale})`,
        opacity: baseOpacity,
      }}
      initial={{
        opacity: 0,
        y: 20,
        scale: 0.95,
      }}
      animate={{
        opacity: baseOpacity,
        y: baseTranslateY,
        scale: baseScale,
      }}
      exit={{
        opacity: 0,
        scale: 1.02,
        transition: {
          duration: 0.3,
          ease: "easeOut",
        },
      }}
      transition={{
        type: "spring",
        stiffness: 120,
        damping: 35,
        mass: 1.2,
        duration: 1.2,
        delay: index * 0.1,
      }}
      whileTap={{
        scale: baseScale - 0.005,
      }}
    >
      <motion.div
        className="tw-drop-shadow-lg hover:tw-drop-shadow-md tw-transition-all tw-duration-200"
        transition={{
          duration: 0.6,
          ease: "easeOut",
        }}
      >
        <Nudge
          variant="default"
          nudge={nudge}
          onClick={onClick}
          onDismiss={onDismiss}
          trackingRef={trackingRef}
          dismissible={true}
        />
      </motion.div>
    </motion.div>
  );
}
